import {lazy, Suspense} from "react";
import { useMsal } from "@azure/msal-react";
import AuthRedirect from "./pages/AuthRedirect";
import './styles/main.scss';
// import components
import ScrollToTop from './components/layout/ScrollToTop';
import Authentication from './pages/Authentication';
import { CookiesProvider } from 'react-cookie';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useUser } from './utils/hooks/useUser';
import { AdminProvider, useAdmin } from './utils/hooks/useAdmin';
import { AuthProvider } from './utils/hooks/useAuth';
import config from '../src/config/config';
import CustomerLayout from "./pages/Customer/CustomerPanel/CustomerPanel";
import { useCustomerList } from "./utils/hooks/useCustomerList";
import MaintenancePage from "./pages/Maintenance/MaintenancePage";
import { useMaintenance } from "./utils/hooks/useMaintenance";
import { isWithinMaintenanceTimeWindow } from "./utils/constants";
import OrderNow from "./pages/Order/OrderNow";
import "react-toastify/dist/ReactToastify.css";
 
//Lazy-Components/code-splitting
const Header = lazy(()=> import("./components/layout/Header"));
const ProductCatalog = lazy(()=> import("./pages/ProductCatalog/ProductCatalog"));
const ProductPage = lazy(()=> import("./pages/ProductDetail/ProductDetail"));
const StockItemPage = lazy(()=> import("./pages/StockItemDetail/StockItemDetail"));
const GroupedItemPage = lazy(()=> import("./pages/GroupItemDetail/GroupItemDetail"));
const CartPage = lazy(()=> import("./pages/Cart/Cart"));
const CheckoutPage = lazy(()=> import("./pages/Checkout"));
const Kits = lazy(()=> import("./pages/Shop"));
const Invoices = lazy(()=> import("./pages/Invoices"));
const Wishlist = lazy(()=> import("./pages/Wishlist"));
const InviteUser = lazy(()=> import("./pages/Admin/InviteUser/InviteUser"));
const AdminLayout = lazy(()=> import("./pages/Admin/AdminPanel/AdminPanel"));
const CustomerSwitcher = lazy(()=> import("./pages/Admin/CustomerSwitcher/CustomerSwitcher"));
const CustomerSwitch = lazy(()=> import("./pages/Customer/CustomerSwitch/CustomerSwitch"));
const StockItems = lazy(()=> import("./pages/StockItems"));
const ProductBrand = lazy(()=> import("./pages/ProductBrand/ProductBrand"));
const ImageRefresh = lazy(()=> import("./pages/Admin/ImageRefresh/ImageRefresh"));
 
 
function App() {
 
  return (
    <AuthProvider>
      <AdminProvider>
        <AppContent />
      </AdminProvider>
    </AuthProvider>
  );
}
 
function AppContent() {
  const location = useLocation();
  // const showHeaderFooter = location.pathname !== '/checkout';
  const showHeaderFooter = true;
  const { accounts, inProgress,instance } = useMsal();
  const { fetchCustomer, customer, error } = useUser();
  const { setIsAdmin, isAdmin } = useAdmin();
  const [customerError, setCustomerError] = useState("");
  const [customerFetched, setCustomerFetched] = useState(false);
  const { setIsCustomer, isCustomer } = useCustomerList();
  const { loadMaintenance ,isActive: isMaintenance, details} = useMaintenance();
  const [blockedUser, setBlockedUser] = useState(false);
  const navigate = useNavigate();

  //maintenance
  useEffect(() => {
    loadMaintenance();
  }, [loadMaintenance]);

  // Handle authenticated user flow
  useEffect(() => {
    if (accounts && accounts.length > 0) {
        const idToken = accounts[0]?.idTokenClaims;
        const groups = idToken?.groups || [];
        const isAdminUser = groups.includes(config.AZURE_GROUP_ID);
        const isBasicUser = groups.includes(config.AZURE_BASIC_ID);
        setIsAdmin(isAdminUser);
        // block user if not in any group
       if (!isAdminUser && !isBasicUser) {
           setBlockedUser(true);
       }

    } else {
      setIsAdmin(false);
    }
 
    if (
      accounts &&
      accounts.length > 0 &&
      !customerFetched &&
      !customer
    ) {
      const email = accounts[0]?.username;
      if (email) {
        setCustomerFetched(true);
        (async () => {
          const result = await fetchCustomer(email, undefined);
          if (!result?.payload?.customerId) {
            setCustomerError("Customer not found or missing customer number.");
          } else {
            setCustomerError("");
          }
        })();
      }
    }
  }, [accounts, customer, fetchCustomer, customerFetched, setIsAdmin]);

  useEffect(() => {
  if (blockedUser) {
    const timer = setTimeout(() => {
      instance.logoutRedirect(); 
    }, 5000); 

    return () => clearTimeout(timer);
  }
}, [blockedUser]);

  const isTimeBasedMaintenance = isWithinMaintenanceTimeWindow(details?.maintenanceStartTime,details?.maintenanceEndTime);

  if ((isMaintenance || isTimeBasedMaintenance) && location.pathname !== "/ordernow") {
    return (
      <Routes>
        <Route path="*" element={<MaintenancePage details={details}/>} />
      </Routes>
    );
  }
  // Show loading only during authentication process, not for unauthenticated users
  if (accounts && accounts.length > 0 && inProgress !== "none") {
    return <div>Loading authentication...</div>;
  }
 
  // Only show error if user is authenticated but customer data is missing
  if (accounts && accounts.length > 0 && customerError) {
    return <div style={{ color: 'red', textAlign: 'center', marginTop: '2rem' }}>{customerError}</div>;
  }
  
  if (accounts && accounts.length > 0 && location.pathname === "/ordernow") {
    return <Navigate to="/productCatalog" replace />;
  }

  if (blockedUser) {
  return (
    <div style={{
      height: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "26px",
      fontWeight: "bold"
    }}>
      You are not authorized. Redirecting to login...
    </div>
  );
}

  return (
     <CookiesProvider>
      <ScrollToTop />
      {showHeaderFooter && !location.pathname.includes("ordernow") && <Suspense fallback={<h1></h1>}><Header/></Suspense> }
      <Routes>
        <Route path="/" element={<Navigate to="/productCatalog" replace />} />
        <Route path="/:id" element={<Suspense fallback={<h1>Loading...</h1>}><ProductPage /></Suspense>} />
        <Route path="items/:id" element={<Suspense fallback={<h1>Loading...</h1>}><StockItemPage /></Suspense>} />
        <Route path="groupeditems/:id" element={<Suspense fallback={<h1>Loading...</h1>}><GroupedItemPage /></Suspense>} />
        <Route path="/authentication" element={<Authentication />} />
       
        {/* Protected routes - require authentication */}
        {accounts && accounts.length > 0 ? (
          <>
            <Route path="/cart" element={<Suspense fallback={<h1>Loading...</h1>}><CartPage /></Suspense>} />
            <Route path="/checkout" element={<Suspense fallback={<h1>Loading...</h1>}><CheckoutPage /></Suspense>} />
            <Route path="/kits" element={<Suspense fallback={<h1>Loading...</h1>}><Kits /></Suspense>} />
            <Route path="/invoices" element={<Suspense fallback={<h1>Loading...</h1>}><Invoices /></Suspense>} />
            <Route path="/wishlist" element={<Suspense fallback={<h1>Loading...</h1>}><Wishlist /></Suspense>} />
            {isAdmin && <Route path="/admin" element={<Suspense fallback={<h1>Loading...</h1>}><AdminLayout /></Suspense>} >
              <Route index element={<Navigate to="inviteuser" replace />} />
              <Route path="inviteuser" element={<Suspense fallback={<h1>Loading...</h1>}><InviteUser /></Suspense>} />
              <Route path="customerswitcher" element={<Suspense fallback={<h1>Loading...</h1>}><CustomerSwitcher /></Suspense>} />
              <Route path="imagerefresh" element={<Suspense fallback={<h1>Loading...</h1>}><ImageRefresh /></Suspense>} />
            </Route>}
            <Route path="/customer" element={<CustomerLayout />} >
              <Route index element={<Navigate to="customerswitch" replace />} />
              <Route path="customerswitch" element={<Suspense fallback={<h1>Loading...</h1>}><CustomerSwitch /></Suspense>} />
            </Route>
          </>
        ) : (
          <>
            <Route path="/cart" element={<Navigate to="/login" replace />} />
            <Route path="/checkout" element={<Navigate to="/login" replace />} />
            <Route path="/kits" element={<Navigate to="/login" replace />} />
            <Route path="/invoices" element={<Navigate to="/login" replace />} />
            <Route path="/wishlist" element={<Navigate to="/login" replace />} />
            <Route path="/admin/*" element={<Navigate to="/login" replace />} />
          </>
        )}
       
        {/* Public routes */}
        <Route path="/login" element={<AuthRedirect />} />
        <Route path="/productCatalog" element={<Suspense fallback={<h1></h1>}><ProductCatalog /></Suspense>} />
        <Route path="/brands" element={<Suspense fallback={<h1></h1>}><ProductBrand /></Suspense>} />
        <Route path="/items" element={<Suspense fallback={<h1></h1>}><StockItems /></Suspense>} />
        <Route path="/ordernow" element={<OrderNow />}/>
        <Route path="*" element={<Navigate to="/productCatalog" replace />} />
      </Routes>
      {/* {showHeaderFooter && <Footer />} */}
    </CookiesProvider>
  );
}
 
export default App;